import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Stack
} from '@mui/material';
import { Home as HomeIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import LeagueGrid from '../components/league/LeagueGrid';
import { useLeagueData } from '../hooks/useLeagueData';
import unifiedApi from '../utils/unifiedApi';
import { useAuth } from '../contexts/AuthContext';
import OutstandingBalanceCard from '../components/balance/OutstandingBalanceCard';
import NextGameHero from '../components/home/NextGameHero';
import ComingUpAgenda from '../components/home/ComingUpAgenda';
import HomeLeagueList from '../components/home/HomeLeagueList';
import { buildAgendaItems, gameRsvpKey, getHeroGame, getTenantToday, groupAgendaByDay } from '../components/home/homeAgenda';

const Home = () => {
  const [organization, setOrganization] = useState(null);
  const [homeData, setHomeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rsvpState, setRsvpState] = useState({}); // { [agendaKey]: { attending, submitting, error } } — overrides server values after a change
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  // Use the new useLeagueData hook for public API
  const { 
    leagues, 
    loading: leaguesLoading,
    error: leaguesError,
    loadLeagues 
  } = useLeagueData({ 
    isPublic: true,
    includeTeamCounts: true,
    includeGameCounts: true
  });

  useEffect(() => {
    // Don't redirect once the user has moved on (e.g. a tapped chat notification opened the chat).
    let cancelled = false;
    const loadOrganization = async () => {
      try {
        setLoading(true);
        const orgData = await unifiedApi.getOrganization();
        
        if (!orgData) {
          setError('Organization not found or not public');
          return;
        }

        setOrganization(orgData);

        // Load leagues and authenticated home data in parallel
        await Promise.all([
          loadLeagues(),
          isAuthenticated
            ? unifiedApi.getMyHome().then(setHomeData).catch(() => setHomeData(null))
            : Promise.resolve()
        ]);

        // If there's only one league, redirect to it
        if (!cancelled && orgData.leagues && orgData.leagues.length === 1) {
          navigate(`/leagues/${orgData.leagues[0].slug}/standings`);
          return;
        }
      } catch (err) {
        setError('Failed to load organization information');
        console.error('Error loading organization:', err);
      } finally {
        setLoading(false);
      }
    };

    loadOrganization();
    return () => { cancelled = true; };
  }, [navigate, loadLeagues, isAuthenticated]);

  const handleRsvp = useCallback(async (item, attending) => {
    const setItemState = (patch) => setRsvpState((prev) => ({
      ...prev,
      [item.key]: { ...prev[item.key], ...patch },
    }));

    setItemState({ submitting: true, error: null });
    try {
      if (item.kind === 'social') {
        await unifiedApi.rsvpToSocialEvent(item.id, attending);
      } else if (item.kind === 'spare') {
        await unifiedApi.setSparePoolMemberAvailability(item.sparePoolMemberId, [
          { spareTimeSlotId: item.timeSlotId, isAvailable: attending },
        ]);
      } else {
        const saved = await unifiedApi.upsertGameRsvp(item.gameId, homeData?.userTenantId, attending);
        if (!saved) throw new Error('Game not found');
      }
      setItemState({ attending, submitting: false, error: null });
    } catch {
      setItemState({ submitting: false, error: 'Couldn\'t save. Please try again.' });
    }
  }, [homeData]);

  const canRsvp = useCallback((item) => {
    if (item.kind === 'social') return true;
    if (item.kind === 'spare') return !!(item.sparePoolMemberId && item.timeSlotId);
    return !!(item.gameId && homeData?.userTenantId);
  }, [homeData]);

  const getLeagueId = (league) => league.id ?? league.leagueId;

  const handleLeagueNavigate = (league) => {
    if (league.slug) {
      navigate(`/leagues/${league.slug}/standings`);
    } else {
      console.error('League slug is missing:', league);
    }
  };

  const leagueById = (leagues || []).reduce((acc, league) => {
    const id = getLeagueId(league);
    if (id) acc[id] = league;
    return acc;
  }, {});

  const leagueSlugById = Object.fromEntries(
    Object.entries(leagueById).map(([id, league]) => [id, league.slug])
  );

  const filterLeaguesByIds = (ids = []) => ids
    .map((id) => leagueById[id])
    .filter(Boolean);

  const myLeagues = filterLeaguesByIds(homeData?.myActiveLeagueIds);
  const otherLeagues = filterLeaguesByIds(homeData?.otherActiveLeagueIds);
  const inactiveOrPastLeagues = filterLeaguesByIds(homeData?.inactiveOrPastLeagueIds);
  const showGroupedLeagues = isAuthenticated && homeData;

  const heroGame = getHeroGame(homeData?.myNextGames || []);
  const agendaGroups = groupAgendaByDay(buildAgendaItems(homeData));
  const tenantToday = getTenantToday(homeData?.timezone);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box mt={4}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!organization) {
    return (
      <Box mt={4}>
        <Alert severity="info">No organization data available</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Organization Header */}
      <Box className="page-header">
        <Typography variant="h4" component="h1" className="page-title">
          <HomeIcon color="primary" />
          {organization.name}
        </Typography>
      </Box>

      {showGroupedLeagues ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) 320px' },
            gap: 3,
            alignItems: 'start',
            mb: 4,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            {heroGame && (
              <NextGameHero
                game={heroGame}
                rsvp={rsvpState[gameRsvpKey(heroGame)]}
                today={tenantToday}
                canRsvp={canRsvp({ ...heroGame, kind: 'game' })}
                onRsvp={(attending) => handleRsvp({ ...heroGame, kind: 'game', key: gameRsvpKey(heroGame) }, attending)}
              />
            )}
            <ComingUpAgenda
              groups={agendaGroups}
              rsvpState={rsvpState}
              onRsvp={handleRsvp}
              canRsvp={canRsvp}
              leagueSlugById={leagueSlugById}
              today={tenantToday}
            />
          </Box>

          <Stack spacing={3} sx={{ minWidth: 0 }}>
            <OutstandingBalanceCard />
            <HomeLeagueList
              myLeagues={myLeagues}
              otherLeagues={otherLeagues}
              pastLeagues={inactiveOrPastLeagues}
              loading={leaguesLoading}
              error={leaguesError}
            />
          </Stack>
        </Box>
      ) : (
        <>
          {isAuthenticated && <OutstandingBalanceCard sx={{ mb: 4 }} />}
          {/* Leagues Section (anonymous fallback) */}
          {organization?.leagues && organization.leagues.length > 0 && (
            <Box mb={4}>
              <Typography variant="h6" component="h2" className="section-title">
                Leagues
              </Typography>
              <Grid container spacing={3}>
                <LeagueGrid
                  leagues={leagues}
                  loading={leaguesLoading}
                  error={leaguesError}
                  isPublic={true}
                  onLeagueNavigate={handleLeagueNavigate}
                  emptyMessage="No public leagues available."
                  loadingSkeletonCount={3}
                />
              </Grid>
            </Box>
          )}
        </>
      )}

      {/* No Leagues Message */}
      {organization && (!organization.leagues || organization.leagues.length === 0) && (
        <Box mt={4}>
          <Alert severity="info">
            No public leagues are currently available for this organization.
          </Alert>
        </Box>
      )}
    </Box>
  );
};

export default Home;
